"use client";

import { forwardRef, type Ref } from "react";

const LIGHT_LINE_ANGLES = [0, 50, 100, 150];
const HEADLINE_LINE_ONE = ["This", "is"];
const HEADLINE_LINE_TWO = "Language+";
const SUBHEADLINE =
  "The future experience for English learners built with A.I. at each level.";

const heroWordStyle: React.CSSProperties = {
  backgroundSize: "100% 130%, 100% 130%",
  backgroundPosition: "center top, center top",
  backgroundImage:
    "linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.1) 100%), linear-gradient(180deg, #fff 0%, #b7a8ea 100%)",
  opacity: 0,
  filter: "blur(16px)",
  transform: "translateY(20px)",
};

const heroWordClass =
  "inline-block overflow-visible bg-clip-text text-transparent pb-[0.28em] md:pb-[0.18em]";

interface HeroOverlayProps {
  textRef?: Ref<HTMLDivElement>;
}

const HeroOverlay = forwardRef<HTMLDivElement, HeroOverlayProps>(function HeroOverlay({ textRef }, ref) {
  return (
    <div ref={ref} className="absolute inset-0 pointer-events-none">
      {/* Glow ellipse centered behind the phone */}
      <img
        data-hero-glow=""
        src="/assets/hero/glow-ellipse.svg"
        alt=""
        className="absolute z-[1] w-[min(80vw,360px)] h-[min(55vh,420px)] md:w-1/2 md:h-full max-md:[transform:translate(calc(-50%-40px+var(--glow-tx,0px)),calc(-16%+64px+var(--glow-ty,0px)))] md:[transform:translate(calc(-50%-40px+var(--glow-tx,0px)),calc(-18%+52px+var(--glow-ty,0px)))]"
        style={{
          left: "50%",
          top: "50%",
          opacity: 0,
        }}
        draggable={false}
      />

      {/* Light lines crossing behind the phone */}
      <div
        className="absolute z-[2]"
        style={{
          left: "50%",
          top: "58%",
        }}
      >
        {LIGHT_LINE_ANGLES.map((deg) => (
          <img
            key={deg}
            src="/assets/hero/light-line.svg"
            alt=""
            className="absolute"
            style={{
              width: "clamp(120px, 14vw, 200px)",
              transform: `translate(-50%, -50%) rotate(${deg}deg)`,
              opacity: 0.5,
            }}
            draggable={false}
          />
        ))}
      </div>

      {/* Title and subtitle */}
      <div
        ref={textRef}
        className="absolute left-1/2 -translate-x-1/2 text-center z-[15] w-full px-6 overflow-visible pb-2"
        style={{ top: "19%" }}
      >
        <h1
          className="font-bold leading-none overflow-visible flex flex-col items-center max-md:gap-0 md:flex-row md:flex-wrap md:items-baseline md:justify-center md:gap-x-[0.25em] md:leading-[1]"
          style={{ fontSize: "clamp(40px, 11vw, 80px)" }}
        >
          <span className="inline-flex items-baseline justify-center gap-x-[0.25em]">
            {HEADLINE_LINE_ONE.map((word) => (
              <span key={word} data-hero-word="" className={heroWordClass} style={heroWordStyle}>
                {word}
              </span>
            ))}
          </span>
          <span
            data-hero-word=""
            className={`${heroWordClass} max-md:-mt-[0.5em] md:mt-0`}
            style={heroWordStyle}
          >
            {HEADLINE_LINE_TWO}
          </span>
        </h1>
        <p
          data-hero-subtitle=""
          className="mt-2 md:mt-3 mx-auto max-w-[720px] text-center text-white/80 font-normal"
          style={{
            fontSize: "clamp(14px, 3.8vw, 18px)",
            lineHeight: "1.5",
            opacity: 0,
            transform: "translateY(16px)",
          }}
        >
          {SUBHEADLINE}
        </p>
      </div>

    </div>
  );
});

export default HeroOverlay;
